/**
 * Reusable confirmation modal for "you have unsaved changes — what now?".
 * Three actions: Discard, Cancel, Save. Used by IssueSheet at close time
 * for the card-dirty + comment-dirty combos, but the component is generic
 * enough to land in shared/ui so other editors can reuse it.
 */
import React from 'react';
import styled from '@emotion/styled';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 8, 14, 0.4);
`;

const Card = styled.div`
  width: min(420px, calc(100vw - 32px));
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  background: var(--bg-surface);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.28);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Hint = styled.span`
  font-size: 13px;
  color: var(--text-tertiary);
  line-height: 1.5;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ActBtn = styled.button`
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--text-secondary);
  transition: all 0.1s ease;
  &:hover { background: var(--bg-hover); color: var(--text-primary); }
`;

type Props = {
  title?: string;
  message: string;
  discardLabel?: string;
  cancelLabel?: string;
  saveLabel?: string;
  onDiscard: () => void;
  onCancel: () => void;
  onSave: () => void;
};

export const UnsavedChangesPrompt: React.FC<Props> = ({
  title = 'Сохранить изменения?',
  message,
  discardLabel = 'Не сохранять',
  cancelLabel = 'Отмена',
  saveLabel = 'Сохранить',
  onDiscard,
  onCancel,
  onSave,
}) => (
  <Backdrop onClick={onCancel}>
    <Card onClick={(event) => event.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title>{title}</Title>
        <button type="button" onClick={onCancel} aria-label="Закрыть" style={{
          width: 28, height: 28, borderRadius: 7, border: '1px solid var(--border-subtle)',
          background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8"/></svg>
        </button>
      </div>
      <Hint>{message}</Hint>
      <Footer>
        <ActBtn onClick={onDiscard}>{discardLabel}</ActBtn>
        <ActBtn onClick={onCancel}>{cancelLabel}</ActBtn>
        <ActBtn
          onClick={onSave}
          style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
        >
          {saveLabel}
        </ActBtn>
      </Footer>
    </Card>
  </Backdrop>
);
